import {useCallback, useRef, useState} from "react";
import {getFirstScrollingAncestor} from "../../../core/services/utils/utils";
import ObserversService from "../../../core/services/observers";
import useRefEffect from "../../../packages/data-grid/ui/hooks/use-ref-effect";

/**
 * A hook that observes the intersection of the element with the viewport
 *
 * @returns {[ref: React.RefCallback<T>, isIntersecting: boolean]}
 * @template T extends HTMLElement
 */
const useIntersectionObserver = () => {
    const observerRef = useRef();
    const [isIntersecting, setIsIntersecting] = useState(false);

    /**
     * Observes the entries to check if the element is visible
     * @param entries
     */
    const setVisibility = useCallback((entries) => {
        setIsIntersecting(entries.some(e => e.isIntersecting));
    }, [])

    /**
     * Attaches an intersection observer to check if the element is visible
     *
     * @param {T} element
     * @return {function(): void}
     *
     * @template T
     */
    const refEffect = (element) => {
        if (!observerRef.current) {
            /**@type {Partial<IntersectionObserverInit>}*/
            const config = {
                root: getFirstScrollingAncestor(element),
                rootMargin: '20%',
                threshold: 0,
            }
            observerRef.current = ObserversService.newIntersectionObserver(setVisibility, config);
        }
        const observer = observerRef.current;
        ObserversService.observeIntersectionObserver(observer, element);
        return () => ObserversService.unobserveIntersectionObserver(observer, element);
    }

    const ref = useRefEffect(refEffect);

    return [ref, isIntersecting];
}

export default useIntersectionObserver;